import React, { useEffect, useRef, useState } from "react"
import { useEditor, useValue } from "../../editor-context"
import Portal from "@kit/design/portal"
import useSlateDOMNode from "../../hooks/use-slate-dom-node"
import cx from "classnames"
import "./index.css"
import useEventListener from "../../hooks/use-event-listener"

const unknown = { top: 0, left: 0 }

const getPosition = (target, element) => {
  if (!target) return unknown
  if (!element) return unknown
  if (!element.offsetParent) return unknown

  const targetRect = target.getBoundingClientRect()
  const offsetParent = element.offsetParent
  const offsetRect = offsetParent.getBoundingClientRect()

  // If space from target to top of window is greater than height of element (toolbar)
  const isEnoughSpaceAboveElement =
    targetRect.top + offsetParent.scrollTop > element.offsetHeight
  const top = isEnoughSpaceAboveElement
    ? targetRect.top -
      offsetRect.top -
      element.offsetHeight -
      8 +
      offsetParent.scrollTop
    : targetRect.bottom - offsetRect.top + 8 + offsetParent.scrollTop

  // If the target rect is too small or too close to the left edge,
  // we want to make sure the toolbar is not off the screen to the left.
  // And also add 10px so it looks a bit better.
  const minimumLeft = element.getBoundingClientRect().width / 2 + 10
  const left = Math.max(
    targetRect.left - offsetRect.left + targetRect.width / 2,
    minimumLeft
  )

  // Pass CSS custom properties to handle positioning of toolbar arrow in CSS
  return {
    top,
    left,
    "--top": isEnoughSpaceAboveElement ? "100%" : "-12px",
    "--top-after": isEnoughSpaceAboveElement ? "100%" : "-8px",
    "--rotate": isEnoughSpaceAboveElement ? "0deg" : "180deg"
  }
}

const ElementToolbar = ({ renderNode }) => {
  const [active, setActive] = useState(false)
  const toolbar = useRef()
  const editor = useEditor()
  const value = useValue()

  const element = useSlateDOMNode(value.focusBlock)
  const container = useSlateDOMNode(value.document)

  const children = renderNode(editor, value.focusBlock)

  // Close the popover on clicks outside the editor that aren't in the popover.
  useEventListener(
    document,
    "click",
    event => {
      if (
        !container?.contains(event.target) && // outside editor
        !toolbar.current.contains(event.target) && // outside popover
        document.body.contains(event.target) // still inside the document
      ) {
        setActive(false)
      }
    },
    [container]
  )

  const captureClick = event => {
    event.stopPropagation()
  }

  useEffect(() => {
    if (element && value.selection.isFocused && children) {
      setActive(true)
    }
  }, [element, value.selection.isFocused])

  const className = cx("element-toolbar absolute", {
    active: active && children
  })

  const position = getPosition(element, toolbar.current)

  return (
    <Portal>
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events -- onClick stops propagation to keep the toolbar open; no keyboard equivalent */}
      <div
        role="toolbar"
        className={className}
        ref={toolbar}
        onClick={captureClick}
        style={{ ...position, zIndex: 5 }}
      >
        {children}
      </div>
    </Portal>
  )
}

export default ElementToolbar
